import React, {
   createContext,
   useCallback,
   useContext,
   useEffect,
   useMemo,
   useRef,
   useState,
} from "react";
import {
   ActivityReservationInternalClient,
   ActivityReservationInternalModel,
   CountriesClient,
   CountryCode,
   PortalClient,
} from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import AccountContext from "../../../../AccountContext";
import { getMembersFromActivities } from "../../helpers";
import { User } from "../../type";
import { UserPictureCache } from "Hooks";
import { filterByCountry, filterByReservationMethod, filterByState, filterByType, filterByUsers } from "./helpers";
import { toISOStringUnspecified } from "Utils/date";
import { ActivitiesType } from "./type";
import { DateOnly } from "typings/DateTime";
import moment from "moment";
import { NOT_DELETED_FILTER } from "Utils/ActivityReservationStatusFilterPresets";
import UserContext from "features/EnterprisePortal/UserContext";
import { ConnectionsContext } from "Components/Connections/ConnectionsContext";

export interface IHomebaseCountry {
   code: string;
   name: string | undefined;
}

export interface ActivityPrebinOptions {
   venueId: Guid,
   selectedDate?: DateOnly
}

export interface ActivityFilter {
   startDate: string;
   endDate: string;
   state?: string;
   users: User[];
   type: ActivitiesType;
}

export interface ActivityContextValue {
   loading: boolean;
   countryCodes: CountryCode[],
   activities: ActivityReservationInternalModel[];
   filter: ActivityFilter;
   modalContainer: HTMLElement;
   setFilter: (
      filter:
         | Partial<ActivityFilter>
         | ((oldFilter: ActivityFilter) => Partial<ActivityFilter>)
   ) => void;
   users: User[];
   userPictureCache: UserPictureCache;
   prebinOptions: ActivityPrebinOptions | undefined,
   openPrebin: (venueId: Guid, selectedDate?: DateOnly) => void,
   closePrebin: () => void,
   reloadActivities: () => void,
   homebaseCountry?: IHomebaseCountry
}

export const INITIAL_FILTER: ActivityFilter = {
   startDate: toISOStringUnspecified(moment().add(-14, "day").startOf("day")),
   endDate: toISOStringUnspecified(moment().add(14, "day").endOf("day")),
   users: [],
   type: ActivitiesType.ALL,
};

export const ActivityContext = createContext<ActivityContextValue>(
   {} as ActivityContextValue
);

export interface ActivityProviderProps { }

export const ActivityProvider: React.FC<ActivityProviderProps> = ({
   children,
}) => {
   const [loading, setLoading] = useState(false);
   const [prebinOptions, setPrebinOptions] = useState<ActivityPrebinOptions>();
   const [activities, setActivities] = useState<ActivityReservationInternalModel[]>([]);
   const [activityFilter, setActivityFilter] =
      useState<ActivityFilter>(INITIAL_FILTER);
   const [countryCodes, setCountryCodes] = useState<CountryCode[]>([])
   const [modalContainer, setModalContainer] = useState<HTMLElement>(document.body);

   const abortControllerRef = useRef<AbortController | undefined>(undefined);
   const activityInternalClient = useApiClient(ActivityReservationInternalClient);
   const countryClient = useApiClient(CountriesClient);
   const account = useContext(AccountContext);
   const { user } = useContext(UserContext);
   const connectionsContext = useContext(ConnectionsContext);

   const userPictureCache = useRef<UserPictureCache>({});

   const closePrebin = useCallback(() => {
      setPrebinOptions(undefined);
   }, [])
   const openPrebin = useCallback((venueId: Guid, selectedDate?: DateOnly) => {
      setPrebinOptions({
         venueId: venueId,
         selectedDate: selectedDate
      })
   }, []);

   const fetchActivities = useCallback(
      async (startDate?: string, endDate?: string) => {
         abortControllerRef.current?.abort();
         const abortController = new AbortController();
         abortControllerRef.current = abortController;

         try {
            setLoading(true);
            const data = await activityInternalClient.reservations(
               account.id,
               NOT_DELETED_FILTER,
               undefined,
               undefined,
               startDate,
               endDate,
               undefined,
               abortController.signal
            );
            setActivities(data);
            setLoading(false);
         } catch {
            setActivities([]);
            if (!abortController.signal.aborted) {
               setLoading(false);
            }
         }
      },
      [activityInternalClient, account]
   );

   const setFilter = useCallback(
      (
         newFilter:
            | Partial<ActivityFilter>
            | ((oldFilter: ActivityFilter) => Partial<ActivityFilter>)
      ) => {
         setActivityFilter((oldFilter) => ({
            ...oldFilter,
            ...(typeof newFilter === "function"
               ? newFilter(oldFilter)
               : newFilter),
         }));
      },
      []
   );

   const reloadActivities = useCallback(() => {
      fetchActivities(activityFilter.startDate, activityFilter.endDate);
   }, [fetchActivities, activityFilter.startDate, activityFilter.endDate])

   useEffect(() => {
      fetchActivities(activityFilter.startDate, activityFilter.endDate);
   }, [fetchActivities, activityFilter.startDate, activityFilter.endDate]);

   const getFullscreenElement = () => (document.fullscreenElement as HTMLElement) || document.body;

   const handleFullscreenChange = () => {
      setModalContainer(getFullscreenElement());
   }

   useEffect(() => {
      const abortController = new AbortController();
      const fetchStates = async (abortSignal: AbortSignal) => {
         try {
            setCountryCodes(await countryClient.listWithStates(true, abortSignal))
         }
         catch (ex) {
            if (!abortSignal.aborted)
               console.log(ex);
         }
      }
      fetchStates(abortController.signal);

      document.addEventListener("fullscreenchange", handleFullscreenChange);
      document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

      setModalContainer(getFullscreenElement());

      return () => {
         document.removeEventListener("fullscreenchange", handleFullscreenChange);
         document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
         abortController.abort();
      }
   }, [])

   const [homebaseCountry, setHomebaseCountry] = useState<IHomebaseCountry>();

   const portalClient = useApiClient(PortalClient);

   useEffect(() => {
      if (!user.isAuthenticated) return;

      const abortController = new AbortController();
      portalClient.getMemberHomebase(account.id, user.id, abortController.signal)
         .then(data => setHomebaseCountry(data.countryCode ? { code: data.countryCode!, name: data.countryName } : undefined));

      return () => abortController.abort();
   }, [user.id]);

   const filteredActivities = useMemo(
      () =>
         activities.filter(
            (activity) =>
               filterByCountry(activity, homebaseCountry?.code ?? "US")
               && filterByReservationMethod(activity)
               && filterByState(activity, activityFilter.state)
               && filterByUsers(activity, activityFilter.users)
               && filterByType(activity, activityFilter.type, user.id, connectionsContext.connections)
         ),
      [activities, activityFilter.state, activityFilter.users, activityFilter.type, user.id, connectionsContext.connections]
   );

   const users = useMemo(
      () => getMembersFromActivities(activities),
      [activities]
   );

   useEffect(
      () => () => {
         abortControllerRef.current?.abort();
      },
      []
   );

   return (
      <ActivityContext.Provider
         value={{
            loading,
            countryCodes,
            activities: filteredActivities,
            filter: activityFilter,
            modalContainer,
            setFilter,
            users,
            userPictureCache: userPictureCache.current,
            prebinOptions,
            openPrebin,
            closePrebin,
            reloadActivities,
            homebaseCountry
         }}
      >
         {children}
      </ActivityContext.Provider>
   );
};
